import React, { useState } from 'react';
import { Box, Text } from 'ink';
import TextInput from 'ink-text-input';
import { Colors } from '../colors.js';

interface SettingsSetupDialogProps {
  onSubmit: (settings: { apiBaseUrl: string; apiKey?: string }) => Promise<void>;
  onCancel: () => void;
  isConnecting?: boolean;
  connectionError?: string;
}

type SetupStep = 'url' | 'apikey' | 'testing' | 'complete';

export const SettingsSetupDialog: React.FC<SettingsSetupDialogProps> = ({
  onSubmit,
  onCancel,
  isConnecting = false,
  connectionError,
}) => {
  const [step, setStep] = useState<SetupStep>('url');
  const [apiUrl, setApiUrl] = useState('http://localhost:8886');
  const [apiKey, setApiKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUrlSubmit = () => {
    if (!apiUrl.trim()) {
      return;
    }

    // Validate URL format
    try {
      new URL(apiUrl);
    } catch {
      // If URL is invalid, try adding http:// prefix
      if (!apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
        try {
          new URL(`http://${apiUrl}`);
          setApiUrl(`http://${apiUrl}`);
        } catch {
          return; // Invalid URL format
        }
      } else {
        return; // Invalid URL format
      }
    }

    setStep('apikey');
  };

  const handleApiKeySubmit = async () => {
    setIsSubmitting(true);
    setStep('testing');

    try {
      await onSubmit({
        apiBaseUrl: apiUrl,
        apiKey: apiKey.trim() || undefined,
      });
      setStep('complete');
    } catch (error) {
      setIsSubmitting(false);
      setStep('apikey');
    }
  };

  const handleSkipApiKey = async () => {
    setIsSubmitting(true);
    setStep('testing');

    try {
      await onSubmit({
        apiBaseUrl: apiUrl,
        apiKey: undefined,
      });
      setStep('complete');
    } catch (error) {
      setIsSubmitting(false);
      setStep('apikey');
    }
  };

  const renderUrlStep = () => (
    <Box flexDirection="column">
      <Box marginBottom={1}>
        <Text color={Colors.AccentBlue}>
          🚀 Welcome to Automagik CLI - Initial Setup
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          Let's configure your API server connection. This will be saved to:
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text color={Colors.Comment}>
          ~/.automagik-cli/settings.json
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          Please enter your Automagik Hive API server URL:
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text color={Colors.Comment}>
          Examples: http://localhost:8886, https://api.example.com, 192.168.1.100:8080
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text color={Colors.AccentCyan}>API Server URL: </Text>
        <TextInput
          value={apiUrl}
          onChange={setApiUrl}
          onSubmit={handleUrlSubmit}
          placeholder="http://localhost:8886"
        />
      </Box>
      
      <Box>
        <Text color={Colors.Comment}>
          Press Enter to continue, or Ctrl+C to cancel
        </Text>
      </Box>
    </Box>
  );

  const renderApiKeyStep = () => (
    <Box flexDirection="column">
      <Box marginBottom={1}>
        <Text color={Colors.AccentBlue}>
          🔑 API Key Configuration (Optional)
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          API Server: <Text color={Colors.AccentGreen}>{apiUrl}</Text>
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          Do you have an API key for authentication? (Leave empty if not required)
        </Text>
      </Box>
      
      {connectionError && (
        <Box marginBottom={1}>
          <Text color={Colors.AccentRed}>
            ❌ Connection Error: {connectionError}
          </Text>
        </Box>
      )}
      
      <Box marginBottom={1}>
        <Text color={Colors.Comment}>
          API keys typically look like: hive_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text color={Colors.AccentCyan}>API Key: </Text>
        <TextInput
          value={apiKey}
          onChange={setApiKey}
          onSubmit={handleApiKeySubmit}
          placeholder="Optional - press Enter to skip"
          mask="*"
        />
      </Box>
      
      <Box marginBottom={1}>
        <Text color={Colors.Comment}>
          Press Enter to test connection • Ctrl+U to go back • Ctrl+C to cancel
        </Text>
      </Box>
      
      <Box>
        <Text color={Colors.Comment}>
          💡 You can always update these settings later
        </Text>
      </Box>
    </Box>
  );

  const renderTestingStep = () => (
    <Box flexDirection="column">
      <Box marginBottom={1}>
        <Text color={Colors.AccentBlue}>
          🔄 Testing Connection...
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          Server: <Text color={Colors.AccentCyan}>{apiUrl}</Text>
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          API Key: <Text color={Colors.AccentCyan}>
            {apiKey ? '••••••••••••••••' : 'None (no authentication)'}
          </Text>
        </Text>
      </Box>
      
      <Box>
        <Text color={Colors.AccentYellow}>
          ⏳ Validating server connection and saving settings...
        </Text>
      </Box>
    </Box>
  );

  const renderCompleteStep = () => (
    <Box flexDirection="column">
      <Box marginBottom={1}>
        <Text color={Colors.AccentGreen}>
          ✅ Setup Complete!
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          Your settings have been saved to ~/.automagik-cli/settings.json
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          Server: <Text color={Colors.AccentGreen}>{apiUrl}</Text>
        </Text>
      </Box>
      
      <Box marginBottom={1}>
        <Text>
          API Key: <Text color={Colors.AccentGreen}>
            {apiKey ? 'Configured' : 'Not required'}
          </Text>
        </Text>
      </Box>
      
      <Box>
        <Text color={Colors.Comment}>
          🚀 Starting Automagik CLI...
        </Text>
      </Box>
    </Box>
  );

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleInput = (input: string, key: any) => {
      if (step === 'apikey') {
        if (key.ctrl && key.name === 'u') {
          // Go back to URL step
          setStep('url');
        }
      }
    };

    // Note: This is a simplified approach. In a real implementation,
    // you might need to use a more sophisticated input handling mechanism
    // that's compatible with Ink's architecture
    
    return () => {
      // Cleanup if needed
    };
  }, [step]);

  switch (step) {
    case 'url':
      return renderUrlStep();
    case 'apikey':
      return renderApiKeyStep();
    case 'testing':
      return renderTestingStep();
    case 'complete':
      return renderCompleteStep();
    default:
      return renderUrlStep();
  }
};